import React, { useEffect, useMemo, useState } from "react";
import { Eye } from "lucide-react";
import { getSkill } from "../../../lib/inventorySkills";
import { useSeasonStats } from "../../../lib/seasonStats";
import { computeFinishing, useFinishingLibrary } from "../../../lib/finishing";
import { FinishingStatsPanel } from "./FinishingStatsPanel";
import { FinishingOutcomes } from "./FinishingOutcomes";
import { PlanLink } from "../PlanLink";
import { ItemRow } from "../ItemRow";

export const LINKED_SKILLS = { Finishing: ["TEC-BH-08"] };   // Driving ability / control — same rating record as Ball handling
const EMPTY = { outcome_tags: [], finish_types: {}, feel: [], mindset: [], limiting_factors: [], base_finish_strengths: [] };

// Finishing category = 4 sections around the existing Anchor / Good / Rate Limiter rows. Template for the other Technical categories.
export function FinishingCategory({ playerId, items, byId, finishing, onFinishingChange, rowProps, readOnly, canEdit }) {
  const lib = useFinishingLibrary();
  const { records } = useSeasonStats(playerId);
  const fin = finishing || EMPTY;
  const [season, setSeason] = useState(fin.season || null);
  const [cutoffs, setCutoffs] = useState(null);
  useEffect(() => { if (lib && !cutoffs) setCutoffs(lib.cutoffs); }, [lib, cutoffs]);
  useEffect(() => { if (!season && records.length) setSeason(fin.season && records.some(r => r.season === fin.season) ? fin.season : records[0].season); }, [records, season, fin.season]);
  useEffect(() => { if (season && season !== fin.season && !readOnly) onFinishingChange(f => ({ ...f, season })); }, [season]); // eslint-disable-line react-hooks/exhaustive-deps
  const rec = records.find(r => r.season === season);
  const result = useMemo(() => (cutoffs ? computeFinishing(rec?.stats, cutoffs) : null), [rec, cutoffs]);
  const look = new Set(result?.pointsTo || []);
  const linked = (LINKED_SKILLS.Finishing || []).map(getSkill).filter(s => s && s.status === "active");
  if (!lib) return <div className="text-[10px] text-[#757575]">Loading Finishing…</div>;
  const row = (s, fromCat) => (
    <div key={s.id} className="relative" data-testid={`fn-skill-${s.id}`}>
      {(look.has(s.id) || fromCat) && (
        <div className="absolute right-3 -top-2 z-10 flex items-center gap-1.5">
          {look.has(s.id) && <span className="flex items-center gap-1 px-1.5 py-0.5 bg-[#f59e0b] text-[#121212] text-[8px] uppercase tracking-[0.2em] font-bold" data-testid={`fn-look-${s.id}`}><Eye className="w-3 h-3" /> Look at this</span>}
          {fromCat && <span className="px-1.5 py-0.5 border border-white/20 bg-[#1c1c1c] text-[#a0a0a0] text-[8px] uppercase tracking-[0.2em]" data-testid={`fn-linked-${s.id}`}>From {fromCat}</span>}
        </div>
      )}
      <ItemRow skill={s} rating={byId[s.id]} readOnly={readOnly} {...rowProps(s)} />
    </div>
  );
  return (
    <div className="space-y-3" data-testid="fn-category">
      <FinishingStatsPanel playerId={playerId} records={records} season={season} setSeason={setSeason} result={result} cutoffs={cutoffs || {}} canEdit={canEdit} onCutoffsSaved={setCutoffs} />
      <div className="border border-white/10 bg-[#141414] p-4 space-y-2" data-testid="fn-ratings">
        <div className="flex items-center gap-3 flex-wrap"><span className="text-[9px] font-mono text-[var(--org-accent)]">2</span><div className="text-[10px] uppercase tracking-[0.35em] font-bold text-white">Rate the interactions</div><div className="text-[10px] text-[#757575]">Anchor / Good / Rate Limiter · the stats inform, the coach rates</div></div>
        <div className="space-y-3 pt-1">{items.map(s => row(s, null))}{linked.map(s => row(s, s.category))}</div>
      </div>
      <FinishingOutcomes lib={lib} fin={fin} byId={byId} readOnly={readOnly} onChange={onFinishingChange} />
      <PlanLink playerId={playerId} sectionKey="finishing" />
    </div>
  );
}
